<script lang="ts">
  import ContextMenu from "./ContextMenu.svelte";
  import type { DirStat } from "./dir-stats";
  import { installFileIcons } from "./file-icons";
  import type { FileTableApi } from "./file-list-api";
  import { fileIconKind, fileTypeName } from "./file-type";
  import type { FsRow } from "./fsentry";
  import SortAscending from "./icons/SortAscending.svelte";
  import SortDescending from "./icons/SortDescending.svelte";
  import { sortDown, sortUp } from "./sort";
  import { fmtAge, fmtSize, fmtTimeMs, len } from "./util";

  interface Props {
    sortedEntries: FsRow[];
    // sizes and dates of the directories, by name, as far as they are known
    dirStats: Record<string, DirStat>;
    nameSort: number;
    sizeSort: number;
    lastModSort: number;
    typeSort: number;
    isSelected: (idx: number) => boolean;
    // true if more than one row is selected
    multiSelect: boolean;
    onRowClick: (ev: MouseEvent, idx: number) => void;
    onRowMouseMove: (ev: MouseEvent, idx: number) => void;
    onNameSort: () => void;
    onSizeSort: () => void;
    onLastModSort: () => void;
    onTypeSort: () => void;
    onContextMenu: (ev: MouseEvent) => void;
    currentContextMenu: any;
    cmPageX: number;
    cmPageY: number;
    cmSelectFirst: boolean;
    tableApi: FileTableApi;
  }

  let {
    sortedEntries,
    dirStats,
    nameSort,
    sizeSort,
    lastModSort,
    typeSort,
    isSelected,
    multiSelect,
    onRowClick,
    onRowMouseMove,
    onNameSort,
    onSizeSort,
    onLastModSort,
    onTypeSort,
    onContextMenu,
    currentContextMenu,
    cmPageX,
    cmPageY,
    cmSelectFirst,
    tableApi,
  }: Props = $props();

  let elTable: HTMLElement = $state() as HTMLElement;

  installFileIcons();

  $effect(() => {
    tableApi.scrollToId = (id: string) => {
      const el = elTable?.querySelector(`[data-id="${id}"]`);
      el?.scrollIntoView(false);
    };
  });

  // ages are counted from when the list was last made
  let now = $derived.by(() => {
    sortedEntries;
    return Date.now();
  });

  function fsentrySize(e: FsRow): string {
    if (e.isDir) {
      let st = dirStats[e.name];
      if (!st) {
        return "";
      }
      // "+": there is more that could not be read
      return fmtSize(st.size) + (st.failed ? "+" : "");
    }
    return fmtSize(e.size);
  }

  // a directory has the date of the newest file under it, once it is known
  function fsentryLastMod(e: FsRow): number {
    if (e.isDir) {
      return dirStats[e.name]?.lastMod ?? 0;
    }
    return e.lastMod;
  }

  function fsentrySizeTitle(e: FsRow): string {
    let st = e.isDir ? dirStats[e.name] : undefined;
    if (!st) {
      return "";
    }
    if (!st.done) {
      return "Still counting the files in this directory";
    }
    return st.failed ? "Some directories in it could not be read and are not counted" : "";
  }
</script>

<div id="fm-file-list-wrap" class="grow-1 min-h-0 h-full overflow-auto" oncontextmenu={onContextMenu}>
  {#if len(sortedEntries) > 0}
    <div class="tbl table select-none overflow-y-auto border-collapse w-full" bind:this={elTable}>
      <div class="table-header-group sticky top-0">
        <div class="table-row text-base cursor-pointer">
          <!-- the icons have no title -->
          <div class="table-cell fe-icon bg-gray-100"></div>
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            onclick={onNameSort}
            class="table-cell text-xs text-left pl-1 fe-name bg-gray-100 hover:bg-gray-200 border-r-2 border-gray-300"
          >
            Name
            {#if nameSort == sortDown}
              <SortDescending class="inline" />
            {:else if nameSort == sortUp}
              <SortAscending class="inline" />
            {:else}
              <div style="width: 16; height: 16"></div>
            {/if}
          </div>

          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            onclick={onSizeSort}
            class="table-cell text-xs pr-2 pl-2 text-right fe-fit bg-gray-100 hover:bg-gray-200 border-r-2 border-gray-300"
          >
            {#if sizeSort == sortDown}
              <SortDescending class="inline" />
            {:else if sizeSort == sortUp}
              <SortAscending class="inline" />
            {:else}
              <div style="width: 16; height: 16"></div>
            {/if}
            Size
          </div>

          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            onclick={onTypeSort}
            class="table-cell text-xs text-left pr-2 pl-2 fe-fit bg-gray-100 hover:bg-gray-200 border-r-2 border-gray-300"
          >
            Type
            {#if typeSort == sortDown}
              <SortDescending class="inline" />
            {:else if typeSort == sortUp}
              <SortAscending class="inline" />
            {:else}
              <div style="width: 16; height: 16"></div>
            {/if}
          </div>

          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            onclick={onLastModSort}
            class="table-cell text-xs text-right pr-2 pl-2 fe-fit bg-gray-100 hover:bg-gray-200"
          >
            {#if lastModSort == sortDown}
              <SortDescending class="inline" />
            {:else if lastModSort == sortUp}
              <SortAscending class="inline" />
            {:else}
              <div style="width: 16; height: 16"></div>
            {/if}
            Date
          </div>
        </div>
      </div>

      <div class="table-row-group">
        {#each sortedEntries as e, index (e.id)}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            data-id={e.id}
            class="table-row whitespace-nowrap cursor-pointer cv-auto"
            class:bg-sky-200={isSelected(index)}
            onclick={(ev) => onRowClick(ev, index)}
            onmousemove={(ev) => currentContextMenu || onRowMouseMove(ev, index)}
          >
            <div class="table-cell fe-icon">
              <!-- with several rows selected a check box marks which -->
              <span class="fi fi-{multiSelect && isSelected(index) ? 'checked' : fileIconKind(e.name, e.isDir)}"></span>
            </div>

            <div class="tc table-cell fe-name text-left" class:font-bold={e.isDir}>
              {e.name}
            </div>

            <div
              class="tc table-cell fe-size fe-fit pr-2 text-right"
              class:text-gray-400={e.isDir && dirStats[e.name]?.done === false}
              title={fsentrySizeTitle(e)}
            >
              {fsentrySize(e)}
            </div>

            <div class="tc table-cell fe-type fe-fit pr-2 text-left">
              {fileTypeName(e.name, e.isDir)}
            </div>

            <div
              class="tc table-cell fe-lastmod fe-fit whitespace-pre pr-2 text-right"
              class:text-gray-400={e.isDir && dirStats[e.name]?.done === false}
              title={fsentryLastMod(e) ? fmtTimeMs(fsentryLastMod(e)) : ""}
            >
              {fsentryLastMod(e) ? fmtAge(fsentryLastMod(e), now) : ""}
            </div>
          </div>
        {/each}
      </div>
    </div>
    {#if currentContextMenu}
      <!-- a new menu starts with its own selection -->
      {#key currentContextMenu}
        <ContextMenu pageX={cmPageX} pageY={cmPageY} menu={currentContextMenu} selectFirst={cmSelectFirst} />
      {/key}
    {/if}
  {:else}
    <div class="flex flex-col justify-center items-center h-full">
      <div>No files!</div>
    </div>
  {/if}
</div>

<style lang="postcss">
  .cv-auto {
    content-visibility: auto;
    contain-intrinsic-height: 1rem;
  }

  .tbl {
    font-family: var(--font-monospace);
    font-size: 10pt;
  }

  .tc {
    vertical-align: top;
    padding-left: 0.5rem;
  }
  /* The name column takes the width the other columns leave. max-width: 0
     keeps a long name from widening it: the name is cut with an ellipsis. */
  .fe-name {
    width: 100%;
    max-width: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
  }
  /* the icon of the row, before its name */
  .fe-icon {
    width: 1%;
    padding-left: 0.375rem;
    vertical-align: top;
    line-height: 0;
  }
  .fe-icon :global(.fi) {
    margin-top: 1px;
  }
  /* the size and the time columns are as wide as what is in them */
  .fe-fit {
    width: 1%;
    white-space: nowrap;
  }
</style>
